import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fileToBase64 } from "@/lib/api";
import { useSession } from "@/lib/session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Banknote, Vault, Camera, ArrowDownToLine, AlertCircle, UserCircle, ScanLine, AlertTriangle, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";

const KIND_META = {
  PAYOUT: { label: "Payout · Πληρωμή", color: "text-orange-700", icon: ArrowDownToLine },
  CASH_DROP: { label: "Cash Drop · Μεταφορά", color: "text-emerald-700", icon: Vault },
};

export default function Cashflow() {
  const t = useT();
  const { shift, setVoucherPreview, portalUnlocked, liveViewUnlocked } = useSession();
  const ownerActive = portalUnlocked || liveViewUnlocked;
  const [transactionDate, setTransactionDate] = useState("");
  const nav = useNavigate();
  // Iter 21 — Shift-validation modal. After a successful LOG ENTRY or
  // PAY STAFF submission the raw payload is staged into `pendingDraft`
  // and the dialog opens. The operator then picks "+ ADD TO SHIFT" (fires
  // POST /api/cashflow immediately) or "No, later" (keeps the draft in
  // session so the End-Shift golden rule still blocks closure).
  const [pendingDraft, setPendingDraftLocal] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [kind, setKind] = useState("PAYOUT");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [imgB64, setImgB64] = useState("");
  const [paymentSource, setPaymentSource] = useState("DRAWER");
  const [chequeNumber, setChequeNumber] = useState("");
  const [chequeMaturity, setChequeMaturity] = useState("");
  const [chequeSupplier, setChequeSupplier] = useState("");
  const [busy, setBusy] = useState(false);

  // --- Staff Payment / Advance state -----------------------------------
  const [staffName, setStaffName] = useState("");
  const [staffAmount, setStaffAmount] = useState("");
  const [staffCategory, setStaffCategory] = useState("Salary Payment");
  const [staffPaymentSource, setStaffPaymentSource] = useState("DRAWER");
  const [staffBusy, setStaffBusy] = useState(false);

  // --- AI Voucher Camera ----------------------------------------------
  const voucherCamRef = useRef(null);
  const [voucherBusy, setVoucherBusy] = useState(false);

  const load = async () => {
    if (!shift) return;
    try {
      const { data } = await api.get("/cashflow", { params: { shift_id: shift.id } });
      setEvents(data);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => { load(); }, [shift?.id]); // eslint-disable-line

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgB64(await fileToBase64(file));
  };

  // Replaces the old `stageAndRoute` immediate-redirect helper. Stages
  // the payload locally and opens the validation modal so the operator
  // explicitly decides "ADD TO SHIFT NOW" vs "keep as draft".
  const openValidationModal = (payload, toastMsg) => {
    setPendingDraftLocal({ ...payload, captured_at: new Date().toISOString() });
    setConfirmOpen(true);
    toast.success(toastMsg);
  };

  // "🟢 + ADD TO SHIFT" handler — mirrors the Invoices voucher commit
  // pipeline: POST /api/cashflow → backend auto-deducts from Expected
  // Cash, mirrors the row into Portal OpEx + Master Cheque Calendar
  // when the row is a cheque. Closes the modal on success.
  const confirmAddToShift = async () => {
    if (!pendingDraft || !shift) return;
    setConfirmBusy(true);
    const tId = toast.loading("Adding to active shift…");
    try {
      const body = {
        shift_id: shift.id,
        location: shift.location,
        kind: pendingDraft.kind,
        amount: Number(pendingDraft.amount || 0),
        note: pendingDraft.note || "",
        payment_source: pendingDraft.payment_source || "DRAWER",
        category: pendingDraft.category || null,
        employee_name: pendingDraft.employee_name || null,
        cheque_number: pendingDraft.cheque_number || null,
        cheque_maturity: pendingDraft.cheque_maturity || null,
        cheque_supplier: pendingDraft.cheque_supplier || null,
        transaction_date: pendingDraft.transaction_date || null,
        image_base64: pendingDraft.image_base64 || null,
      };
      await api.post("/cashflow", body);
      toast.success("Added to shift · Καταχωρήθηκε στη βάρδια", { id: tId });
      setConfirmOpen(false);
      setPendingDraftLocal(null);
      // Clear any voucher draft state from session — the row is now
      // committed and the End-Shift gate no longer needs to flag it.
      setVoucherPreview(null);
      // Refresh the in-page ledger so the new row appears immediately.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Add to shift failed", { id: tId });
    } finally {
      setConfirmBusy(false);
    }
  };

  // "🔴 Όχι, αργότερα / No, later" handler — keeps the row as a true
  // draft. Pushes the payload to the session-wide voucherPreview so the
  // Invoices tab surfaces it AND the End-Shift screen's amber banner
  // still blocks SAVE & CLOSE until the operator deals with it.
  const keepAsDraft = () => {
    if (pendingDraft) {
      setVoucherPreview({ ...pendingDraft });
    }
    setConfirmOpen(false);
    toast.message("Kept as draft · Επιστροφή αργότερα", {
      description: "End Shift will still block closure until this is added.",
    });
  };

  const submit = async () => {
    if (!shift) { toast.error("Start a shift first"); return; }
    // Strict null safety — empty/None/0 collapses to 0.00 (Golden Rule)
    const amt = amount === "" || amount == null ? 0.0 : Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    // Universal pipeline — stage the entry, open shift-validation modal.
    openValidationModal(
      {
        _origin: "manual",
        kind,
        amount: amt,
        note,
        category: null,
        employee_name: "",
        payment_source: paymentSource,
        cheque_number: paymentSource === "CHEQUE" ? chequeNumber.trim() : null,
        cheque_maturity: paymentSource === "CHEQUE" ? chequeMaturity : null,
        cheque_supplier: paymentSource === "CHEQUE" ? chequeSupplier.trim() : null,
        transaction_date: ownerActive && transactionDate ? transactionDate : null,
        image_url: imgB64 ? `data:image/jpeg;base64,${imgB64.slice(0, 50)}…` : null,
        image_base64: imgB64 || null,
      },
      `${kind === "PAYOUT" ? "Payout" : "Cash drop"} (${paymentSource === "EXTERNAL" ? "Bank Transfer" : paymentSource === "CHEQUE" ? "Cheque" : "Drawer"}) saved as draft`,
    );
    setAmount(""); setNote(""); setImgB64(""); setPaymentSource("DRAWER");
    setChequeNumber(""); setChequeMaturity(""); setChequeSupplier("");
    setTransactionDate("");
  };

  const submitStaff = async () => {
    if (!shift) { toast.error("Start a shift first"); return; }
    if (!staffName.trim()) { toast.error("Enter the employee's name"); return; }
    const amt = staffAmount === "" || staffAmount == null ? 0.0 : Number(staffAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    openValidationModal(
      {
        _origin: "staff",
        kind: "PAYOUT",
        amount: amt,
        category: staffCategory,
        employee_name: staffName.trim(),
        payment_source: staffPaymentSource,
        note: `${staffName.trim()} · ${staffCategory}`,
      },
      `${staffCategory} for ${staffName.trim()} (${staffPaymentSource === "EXTERNAL" ? "Bank Transfer" : "Drawer"}) saved as draft`,
    );
    setStaffName(""); setStaffAmount(""); setStaffCategory("Salary Payment"); setStaffPaymentSource("DRAWER");
  };

  const handleVoucherCapture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!shift) { toast.error("Start a shift first (Tab 1)"); return; }
    setVoucherBusy(true);
    const t = toast.loading("Scanning voucher with AI…");
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/ocr/voucher", {
        image_base64: b64,
        shift_id: shift.id,
        location: shift.location,
      });
      // Hand off to Invoices tab as a pending draft. The Invoices preview
      // dialog will surface this for explicit '+ ADD TO SHIFT'.
      setVoucherPreview({
        ...data,
        captured_at: new Date().toISOString(),
      });
      toast.success(
        data.category
          ? `Detected: ${data.category} — review in Invoices`
          : "Voucher staged — pick a category in Invoices",
        { id: t }
      );
      nav("/invoices");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Voucher scan failed", { id: t });
    } finally {
      setVoucherBusy(false);
    }
  };

  if (!shift) {
    return (
      <Card className="p-12 max-w-xl mx-auto mt-12 text-center border-dashed border-2" data-testid="cashflow-no-shift">
        <AlertCircle className="h-10 w-10 mx-auto text-slate-400 mb-3" />
        <p className="text-slate-600">No active shift. Please start one from <b>NEW LOGIN</b>.</p>
      </Card>
    );
  }

  const payouts = events.filter((e) => e.kind === "PAYOUT").reduce((s, e) => s + e.amount, 0);
  const drops = events.filter((e) => e.kind === "CASH_DROP").reduce((s, e) => s + e.amount, 0);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">{t("cashflow.title")}</h1>
          <p className="text-slate-500 mt-1 tracking-wide">{t("cashflow.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={voucherCamRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleVoucherCapture}
            data-testid="voucher-cam-input"
          />
          <Button
            onClick={() => voucherCamRef.current?.click()}
            disabled={voucherBusy}
            data-testid="voucher-scan-btn"
            className="min-h-[64px] px-5 bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-bold tracking-wide gap-2 shadow-lg"
            title="Scan a receipt or voucher with the AI camera"
          >
            <ScanLine className="h-5 w-5" />
            {voucherBusy ? "SCANNING…" : "Scan Receipt / Voucher (AI Camera)"}
          </Button>
          <div className="font-mono text-sm text-slate-600 bg-white border border-slate-200 px-4 py-2 rounded-lg">
            {shift.location} · Register {shift.register} · {shift.employee_name}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Form */}
        <Card className="p-6 lg:col-span-1">
          <div className="grid grid-cols-2 gap-2 mb-5">
            <button
              data-testid="kind-payout-btn"
              onClick={() => setKind("PAYOUT")}
              className={`min-h-[64px] rounded-xl border-2 font-bold transition-colors ${
                kind === "PAYOUT" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              <div className="text-base">PAYOUT</div>
              <div className="text-[10px] opacity-70">Πληρωμή</div>
            </button>
            <button
              data-testid="kind-drop-btn"
              onClick={() => setKind("CASH_DROP")}
              className={`min-h-[64px] rounded-xl border-2 font-bold transition-colors ${
                kind === "CASH_DROP" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              <div className="text-base">CASH DROP</div>
              <div className="text-[10px] opacity-70">Μεταφορά &gt; €500</div>
            </button>
          </div>

          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount (€)</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            data-testid="cashflow-amount"
            className="mt-2 min-h-[64px] text-3xl font-mono font-bold text-center border-2"
          />

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">
            Payment Source · Πηγή Πληρωμής
          </Label>
          <Select value={paymentSource} onValueChange={setPaymentSource}>
            <SelectTrigger
              className={`mt-2 min-h-[52px] font-bold border-2 ${
                paymentSource === "EXTERNAL"
                  ? "border-sky-400 bg-sky-50 text-sky-900"
                  : "border-slate-300 text-slate-900"
              }`}
              data-testid="cashflow-payment-source"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DRAWER" data-testid="cashflow-payment-source-drawer">
                Cash from Drawer · Από Ταμείο
              </SelectItem>
              <SelectItem value="EXTERNAL" data-testid="cashflow-payment-source-external">
                External / Bank Transfer · Εκτός Ταμείου
              </SelectItem>
              <SelectItem value="CHEQUE" data-testid="cashflow-payment-source-cheque">
                Cheque · Επιταγή
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid="cashflow-payment-source-hint">
            {paymentSource === "EXTERNAL"
              ? "Bypasses the drawer · routes to Portal OpEx & Tax Matrix only"
              : paymentSource === "CHEQUE"
              ? "Bypasses the drawer · mirrors onto Master Cheque Calendar & OpEx"
              : "Strict shift queue · subtracts from Expected Cash at End Shift"}
          </p>

          {ownerActive && (
            <div className="mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="cashflow-backdate-block">
              <Label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
                🔓 Master Backdate · Custom Transaction Date
              </Label>
              <Input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                data-testid="cashflow-transaction-date"
                className="mt-2 bg-white border-amber-300"
              />
              <p className="text-[10px] text-amber-700 mt-1 leading-snug">
                Owner only · past date routes this entry to the Portal OpEx + Tax Matrix
                quarter that matches the date · does NOT touch the live €700 float or End-Shift metrics.
              </p>
            </div>
          )}

          {paymentSource === "CHEQUE" && (
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="cashflow-cheque-fields">
              <div>
                <Label className="text-[9px] uppercase tracking-wider text-slate-500">Cheque #</Label>
                <Input
                  value={chequeNumber}
                  onChange={(e) => setChequeNumber(e.target.value)}
                  placeholder="123456"
                  className="font-mono"
                  data-testid="cashflow-cheque-number"
                />
              </div>
              <div>
                <Label className="text-[9px] uppercase tracking-wider text-slate-500">Maturity · Λήξη</Label>
                <Input
                  type="date"
                  value={chequeMaturity}
                  onChange={(e) => setChequeMaturity(e.target.value)}
                  data-testid="cashflow-cheque-maturity"
                />
              </div>
              <div>
                <Label className="text-[9px] uppercase tracking-wider text-slate-500">Payee · Δικαιούχος</Label>
                <Input
                  value={chequeSupplier}
                  onChange={(e) => setChequeSupplier(e.target.value)}
                  placeholder="Supplier / payee"
                  data-testid="cashflow-cheque-supplier"
                />
              </div>
            </div>
          )}

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">Note (supplier / driver)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="e.g. Pandora Bakeries delivery driver"
            data-testid="cashflow-note"
            className="mt-2"
          />

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">Receipt photo (optional)</Label>
          <div className="mt-2 flex items-center gap-3">
            <label className="flex-1 min-h-[56px] flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-slate-500 text-slate-600">
              <Camera className="h-5 w-5" />
              <span className="font-semibold">{imgB64 ? "Change Photo" : "Capture Receipt"}</span>
              <input type="file" accept="image/*" capture="environment" onChange={onPickImage} className="hidden" data-testid="cashflow-receipt-input" />
            </label>
            {imgB64 && (
              <img src={`data:image/jpeg;base64,${imgB64}`} alt="" className="h-14 w-14 rounded-md border object-cover" data-testid="cashflow-receipt-preview" />
            )}
          </div>

          <Button
            onClick={submit}
            disabled={busy}
            data-testid="cashflow-submit-btn"
            className="mt-6 w-full min-h-[72px] bg-slate-900 hover:bg-slate-800 text-white text-xl font-bold uppercase tracking-wider"
          >
            {busy ? "Logging…" : "Log Entry"}
          </Button>
        </Card>

        {/* Staff Payment / Advance ----------------------------------- */}
        <Card className="p-6 lg:col-span-1" data-testid="staff-payment-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
              <UserCircle className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-lg leading-tight">Staff Payment / Advance</h3>
              <p className="text-[11px] text-slate-500 tracking-wide">Πληρωμή / Προκαταβολή Προσωπικού</p>
            </div>
          </div>

          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Employee Name · Όνομα</Label>
          <Input
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
            placeholder="e.g. Maria"
            data-testid="staff-name-input"
            className="mt-2 min-h-[52px] text-base font-semibold border-2"
          />

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">Amount (€) · Ποσό</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={staffAmount}
            onChange={(e) => setStaffAmount(e.target.value)}
            placeholder="0.00"
            data-testid="staff-amount-input"
            className="mt-2 min-h-[64px] text-3xl font-mono font-bold text-center border-2"
          />

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">Category · Κατηγορία</Label>
          <Select value={staffCategory} onValueChange={setStaffCategory}>
            <SelectTrigger className="mt-2 min-h-[52px]" data-testid="staff-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Salary Payment">Salary Payment · Μισθοδοσία</SelectItem>
              <SelectItem value="Salary Advance">Salary Advance · Προκαταβολή</SelectItem>
            </SelectContent>
          </Select>

          <Label className="text-[10px] uppercase tracking-wider text-slate-500 mt-4 block">
            Payment Source · Πηγή Πληρωμής
          </Label>
          <Select value={staffPaymentSource} onValueChange={setStaffPaymentSource}>
            <SelectTrigger
              className={`mt-2 min-h-[52px] font-bold border-2 ${
                staffPaymentSource === "EXTERNAL"
                  ? "border-sky-400 bg-sky-50 text-sky-900"
                  : "border-slate-300 text-slate-900"
              }`}
              data-testid="staff-payment-source"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DRAWER" data-testid="staff-payment-source-drawer">
                Cash from Drawer · Από Ταμείο
              </SelectItem>
              <SelectItem value="EXTERNAL" data-testid="staff-payment-source-external">
                External / Bank Transfer · Εκτός Ταμείου
              </SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={submitStaff}
            disabled={staffBusy}
            data-testid="staff-submit-btn"
            className="mt-6 w-full min-h-[72px] bg-indigo-700 hover:bg-indigo-800 text-white text-xl font-bold uppercase tracking-wider"
          >
            {staffBusy ? "Processing…" : "Pay Staff"}
          </Button>

          <p className="text-[11px] text-slate-500 mt-3 leading-snug" data-testid="staff-payment-hint">
            {staffPaymentSource === "EXTERNAL"
              ? "Bank transfer · skips the drawer · routed straight to Portal OpEx & Tax Matrix · Starting Float untouched."
              : "Cash from drawer · auto-deducts from Expected Cash · auto-adds to Portal OpEx Payroll · Starting Float untouched."}
          </p>
        </Card>

        {/* Ledger */}
        <Card className="p-6 lg:col-span-2">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Stat label="Total Payouts" value={`€${payouts.toFixed(2)}`} accent="text-orange-700" testId="stat-payouts" />
            <Stat label="Total Drops" value={`€${drops.toFixed(2)}`} accent="text-emerald-700" testId="stat-drops" />
          </div>
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">This Shift&apos;s Ledger</h3>
          {events.length === 0 ? (
            <div className="p-8 border-2 border-dashed border-slate-200 rounded-xl text-center text-slate-500" data-testid="cashflow-empty">
              No movements yet.
            </div>
          ) : (
            <div className="divide-y divide-slate-200 max-h-[460px] overflow-y-auto">
              {events.map((ev) => {
                const meta = KIND_META[ev.kind];
                const Icon = meta.icon;
                return (
                  <div key={ev.id} className="py-3 flex items-center gap-4" data-testid={`cashflow-row-${ev.id}`}>
                    <div className={`h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center ${meta.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">
                        {meta.label}
                        {ev.category && (
                          <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                            {ev.category}
                          </span>
                        )}
                        {(ev.payment_source || "DRAWER") === "EXTERNAL" && (
                          <span
                            className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 px-2 py-0.5 rounded border border-sky-300"
                            data-testid={`cashflow-row-${ev.id}-external-badge`}
                            title="Bank transfer · bypassed the drawer"
                          >
                            External · Εκτός Ταμείου
                          </span>
                        )}
                        {(ev.payment_source || "DRAWER") === "CHEQUE" && (
                          <span
                            className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded border border-amber-300"
                            data-testid={`cashflow-row-${ev.id}-cheque-badge`}
                            title="Cheque · bypassed the drawer · on Master Cheque Calendar"
                          >
                            Cheque · Επιταγή{ev.cheque_number ? ` #${ev.cheque_number}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{ev.note || "—"} · {new Date(ev.created_at).toLocaleTimeString()}</div>
                    </div>
                    {ev.receipt_image_url && (
                      <img src={fileUrl(ev.receipt_image_url)} alt="" className="h-10 w-10 rounded border object-cover" />
                    )}
                    <div className={`font-mono font-bold text-lg ${meta.color}`}>€{ev.amount.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !confirmBusy && setConfirmOpen(o)}>
        <DialogContent
          className="max-w-lg border-2 border-amber-400"
          data-testid="cashflow-validation-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <span className="text-base sm:text-lg leading-tight">
                🚨 ΕΠΙΚΥΡΩΣΗ ΒΑΡΔΙΑΣ / SHIFT VALIDATION
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-[13px] leading-relaxed">
            <p className="text-slate-800 font-semibold">
              Η εγγραφή αποθηκεύτηκε στα πρόχειρα! Θέλετε να προστεθεί ΤΩΡΑ στα έξοδα της τρέχουσας βάρδιας για να υπολογιστεί στα μετρητά του End Shift;
            </p>
            <p className="text-slate-600">
              Entry saved as draft! Do you want to add it to the active shift layout right now?
            </p>

            {pendingDraft && (
              <div
                className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-[12px] font-mono text-slate-700"
                data-testid="cashflow-validation-summary"
              >
                <div className="flex justify-between">
                  <span className="text-slate-500">Type</span>
                  <span className="font-semibold">
                    {pendingDraft.kind === "PAYOUT" ? "PAYOUT" : "CASH DROP"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-semibold">
                    €{Number(pendingDraft.amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Payment source</span>
                  <span className="font-semibold">{pendingDraft.payment_source}</span>
                </div>
                {pendingDraft.employee_name && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Staff</span>
                    <span className="font-semibold">{pendingDraft.employee_name}</span>
                  </div>
                )}
                {pendingDraft.note && (
                  <div className="mt-1 text-slate-500 italic line-clamp-2">{pendingDraft.note}</div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-4">
            <Button
              onClick={confirmAddToShift}
              disabled={confirmBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 text-base gap-2 shadow-md"
              data-testid="cashflow-validation-add"
            >
              <Check className="h-5 w-5" />
              {confirmBusy ? "Adding…" : "🟢 + ADD TO SHIFT"}
            </Button>
            <button
              type="button"
              onClick={keepAsDraft}
              disabled={confirmBusy}
              className="text-rose-700 hover:text-rose-800 hover:underline font-semibold text-sm px-3 py-2 transition-colors disabled:opacity-50"
              data-testid="cashflow-validation-later"
            >
              🔴 Όχι, αργότερα / No, later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`font-mono text-3xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
